// Filter dialog modal — replaces window.prompt() with proper form-based UI.
// Supports numeric parameters with min/max ranges, sliders, and live preview.
//
// Live preview: pass `sourcePixels` (a 256x256 RGBA sample) and `applyFn`
// (a function that takes params and returns filtered pixels). The preview
// canvas updates in real-time as sliders change.

import { trapFocus, ColorPicker, hexToRgb, rgbToHex } from "./ColorPicker";

export interface FilterParamDef {
  key: string;
  label: string;
  type: "number" | "slider" | "checkbox" | "color" | "dropdown";
  default: number | boolean | string;
  min?: number;
  max?: number;
  step?: number;
  unit?: string;
  /** Options for dropdown type. */
  options?: ReadonlyArray<{ value: string; label: string }>;
}

export interface FilterDialogResult {
  [key: string]: number | boolean | string;
}

export interface FilterDialogOptions {
  title: string;
  params: FilterParamDef[];
  /** Optional source pixel data for live preview (256x256 RGBA). */
  sourcePixels?: Uint8ClampedArray;
  /** Optional function to apply the filter for preview. Takes current params, returns filtered pixels. */
  applyFn?: (params: Record<string, number | boolean>) => Uint8ClampedArray;
  /** Optional callback called after the dialog DOM is ready, for custom field wiring. */
  onReady?: (el: HTMLDivElement, params: FilterParamDef[]) => void;
}

export class FilterDialog {
  private el: HTMLDivElement | null = null;
  private resolve: ((result: FilterDialogResult | null) => void) | null = null;
  private previewCanvas: HTMLCanvasElement | null = null;
  private sourcePixels: Uint8ClampedArray | null = null;
  private applyFn: ((params: Record<string, number | boolean>) => Uint8ClampedArray) | null = null;
  private params: FilterParamDef[] = [];
  private previewScheduled = false;
  /** Active key handler so we can remove it on close. */
  private keyHandler: ((e: KeyboardEvent) => void) | null = null;
  /** Element that had focus before the dialog opened — restored on close (#225). */
  private openerEl: Element | null = null;
  /** Snapshot of initial param values for Alt-Reset (#225). */
  private initialValues: FilterDialogResult = {};
  /** Whether Alt is currently held — used to flip Cancel→Reset (#225). */
  private altHeld = false;
  /**
   * Lazily-created color picker shared by every `type: "color"` swatch in
   * this dialog (#i186). Created on first swatch click and reused for the
   * lifetime of this FilterDialog instance so repeated opens don't leak a
   * fresh modal DOM node each time.
   */
  private colorPicker: ColorPicker | null = null;

  /**
   * Open a filter dialog with the given parameters.
   * Returns the filled-in parameters, or null if cancelled.
   */
  open(title: string, params: FilterParamDef[]): Promise<FilterDialogResult | null>;
  open(options: FilterDialogOptions): Promise<FilterDialogResult | null>;
  open(arg: string | FilterDialogOptions, params?: FilterParamDef[]): Promise<FilterDialogResult | null> {
    this.close();

    let title: string;
    let p: FilterParamDef[];
    let sourcePixels: Uint8ClampedArray | undefined;
    let applyFn: ((params: Record<string, number | boolean>) => Uint8ClampedArray) | undefined;
    let onReady: ((el: HTMLDivElement, params: FilterParamDef[]) => void) | undefined;

    if (typeof arg === "string") {
      title = arg;
      p = params!;
    } else {
      title = arg.title;
      p = arg.params;
      sourcePixels = arg.sourcePixels;
      applyFn = arg.applyFn;
      onReady = arg.onReady;
    }

    this.sourcePixels = sourcePixels ?? null;
    this.applyFn = applyFn ?? null;
    this.params = p;
    // Capture opener for focus restore (#225).
    this.openerEl = document.activeElement;

    // Snapshot initial values for Alt-Reset (#225).
    this.initialValues = {};
    for (const param of p) {
      this.initialValues[param.key] = param.default;
    }

    return new Promise<FilterDialogResult | null>((resolve) => {
      this.resolve = resolve;

      const overlay = document.createElement("div");
      overlay.className = "filter-dialog";
      // ARIA: modal dialog accessibility (#47)
      overlay.setAttribute("role", "dialog");
      overlay.setAttribute("aria-modal", "true");
      overlay.setAttribute("aria-labelledby", "filter-dialog-title");
      const hasPreview = !!(sourcePixels && applyFn);
      overlay.innerHTML = `
        <div class="filter-dialog__sheet">
          <div class="filter-dialog__title" id="filter-dialog-title">${escapeHtml(title)}</div>
          <div class="filter-dialog__body">
            ${hasPreview ? `
            <div class="filter-dialog__preview">
              <canvas class="filter-dialog__preview-canvas" width="256" height="256"></canvas>
              <div class="filter-dialog__preview-label">Preview</div>
            </div>` : ""}
            <div class="filter-dialog__fields">
              ${p.map((param) => this.renderField(param)).join("")}
            </div>
          </div>
          <div class="filter-dialog__actions">
            <button class="filter-dialog__btn filter-dialog__btn--cancel" data-action="cancel">Cancel</button>
            <button class="filter-dialog__btn filter-dialog__btn--primary" data-action="ok">OK</button>
          </div>
        </div>
      `;

      document.body.appendChild(overlay);
      this.el = overlay;

      if (hasPreview) {
        this.previewCanvas = overlay.querySelector<HTMLCanvasElement>(".filter-dialog__preview-canvas");
        this.updatePreview();
      }

      // Wire up scrubby labels for numeric fields (#225).
      const sheet = overlay.querySelector<HTMLElement>(".filter-dialog__sheet")!;
      this.wireScrubbyLabels(overlay);

      // Call custom onReady callback for field wiring (e.g., constrain proportions).
      onReady?.(overlay, p);

      const cancelBtn = overlay.querySelector<HTMLButtonElement>('[data-action="cancel"]')!;

      // Wire up buttons.
      overlay.querySelector<HTMLButtonElement>('[data-action="ok"]')?.addEventListener("click", () => {
        this.doOk(p);
      });
      cancelBtn.addEventListener("click", () => {
        if (this.altHeld) {
          // Alt-click on Reset — restore initial values without closing (#225).
          this.doReset();
        } else {
          this.doCancel();
        }
      });

      // Wire up sliders ↔ number inputs and trigger preview updates.
      for (const param of p) {
        if (param.type === "slider" || param.type === "number") {
          const numInput = overlay.querySelector<HTMLInputElement>(`[data-param="${param.key}"]`);
          const sliderInput = overlay.querySelector<HTMLInputElement>(`[data-param-slider="${param.key}"]`);
          if (numInput && sliderInput) {
            numInput.addEventListener("input", () => {
              sliderInput.value = numInput.value;
              this.schedulePreview();
            });
            sliderInput.addEventListener("input", () => {
              numInput.value = sliderInput.value;
              this.schedulePreview();
            });
          }
        }
        if (param.type === "checkbox") {
          const checkInput = overlay.querySelector<HTMLInputElement>(`[data-param="${param.key}"]`);
          checkInput?.addEventListener("change", () => this.schedulePreview());
        }
        if (param.type === "color") {
          this.wireColorSwatch(overlay, param.key);
        }
      }

      // Keyboard handler: Escape, Enter, Tab, Alt tracking (#225).
      const onKey = (e: KeyboardEvent) => {
        // A color-swatch click (#i186) can open the shared ColorPicker modal
        // on top of this dialog. ColorPicker's own Enter/Escape handling
        // doesn't stop propagation, so without this guard the same keydown
        // would also commit/cancel THIS dialog before the picked color is
        // applied. Defer to the picker while any instance is visible.
        const pickerOpen = Array.from(document.querySelectorAll<HTMLElement>(".color-picker"))
          .some((el) => el.style.display !== "none");
        if (pickerOpen) return;

        if (e.key === "Escape") {
          this.doCancel();
          return;
        }

        // Enter in any field commits the dialog — but NOT when a <select> is open (#225).
        // We detect an open select by checking whether the active element is a <select>
        // and the select element itself has more than one visible option (a heuristic
        // that works in jsdom; real browsers show the native picker but don't fire Enter
        // at the document while it is open — so the guard is belt-and-suspenders).
        if (e.key === "Enter") {
          const active = document.activeElement;
          const isDropdownOpen = active instanceof HTMLSelectElement && active.size > 1;
          if (!isDropdownOpen) {
            this.doOk(p);
          }
          return;
        }

        // Tab cycles within the dialog sheet (#225).
        if (e.key === "Tab") {
          trapFocus(e, sheet);
          return;
        }
      };
      document.addEventListener("keydown", onKey);
      this.keyHandler = onKey;

      // Alt key tracking for Cancel → Reset relabeling (#225).
      const onAltDown = (e: KeyboardEvent) => {
        if (e.key === "Alt") {
          this.altHeld = true;
          cancelBtn.textContent = "Reset";
        }
      };
      const onAltUp = (e: KeyboardEvent) => {
        if (e.key === "Alt") {
          this.altHeld = false;
          cancelBtn.textContent = "Cancel";
        }
      };
      document.addEventListener("keydown", onAltDown);
      document.addEventListener("keyup", onAltUp);

      // Store cleanup handles so close() can remove them.
      (overlay as HTMLDivElement & { _onKey: typeof onKey; _onAltDown: typeof onAltDown; _onAltUp: typeof onAltUp })._onKey = onKey;
      (overlay as HTMLDivElement & { _onAltDown: typeof onAltDown })._onAltDown = onAltDown;
      (overlay as HTMLDivElement & { _onAltUp: typeof onAltUp })._onAltUp = onAltUp;

      // Focus first input.
      const firstInput = overlay.querySelector<HTMLInputElement>("input, select");
      firstInput?.focus();
    });
  }

  /**
   * Wire scrubby-label drag behaviour on every numeric-field label (#225).
   * Dragging horizontally on the label text acts like a scrubby slider (PS).
   */
  private wireScrubbyLabels(overlay: HTMLElement): void {
    const labels = overlay.querySelectorAll<HTMLElement>(".filter-dialog__label[data-scrubby]");
    for (const labelEl of labels) {
      const paramKey = labelEl.dataset.scrubby!;
      const numInput = overlay.querySelector<HTMLInputElement>(`[data-param="${paramKey}"]`);
      const sliderInput = overlay.querySelector<HTMLInputElement>(`[data-param-slider="${paramKey}"]`);
      if (!numInput) continue;

      const min = parseFloat(numInput.min ?? "");
      const max = parseFloat(numInput.max ?? "");
      const step = parseFloat(numInput.step ?? "") || 1;

      labelEl.style.cursor = "ew-resize";
      labelEl.style.userSelect = "none";

      let startX = 0;
      let startVal = 0;
      let dragging = false;

      const onPointerMove = (ev: PointerEvent) => {
        if (!dragging) return;
        const delta = ev.clientX - startX;
        // 1 px of drag = 1 step; feel is adjustable.
        let newVal = startVal + delta * step;
        if (isFinite(min)) newVal = Math.max(min, newVal);
        if (isFinite(max)) newVal = Math.min(max, newVal);
        const rounded = Math.round(newVal / step) * step;
        numInput.value = String(rounded);
        if (sliderInput) sliderInput.value = String(rounded);
        numInput.dispatchEvent(new Event("input", { bubbles: true }));
      };

      const onPointerUp = () => {
        if (!dragging) return;
        dragging = false;
        const capturedId = (labelEl as HTMLElement & { _capturedId?: number })._capturedId;
        if (capturedId !== undefined && labelEl.releasePointerCapture) {
          labelEl.releasePointerCapture(capturedId);
        }
      };

      labelEl.addEventListener("pointerdown", (ev: PointerEvent) => {
        dragging = true;
        startX = ev.clientX;
        startVal = parseFloat(numInput.value) || 0;
        (labelEl as HTMLElement & { _capturedId?: number })._capturedId = ev.pointerId;
        if (labelEl.setPointerCapture) {
          labelEl.setPointerCapture(ev.pointerId);
        }
      });
      labelEl.addEventListener("pointermove", onPointerMove);
      labelEl.addEventListener("pointerup", onPointerUp);
      labelEl.addEventListener("pointercancel", onPointerUp);
    }
  }

  /**
   * Wire a `type: "color"` field's swatch button so clicking it opens the
   * shared `ColorPicker` modal, seeded with the swatch's current hex value.
   * On OK, updates the hidden `data-param` input (read by doOk/doReset) and
   * the swatch's background, then schedules a live-preview refresh (#i186).
   */
  private wireColorSwatch(overlay: HTMLElement, key: string): void {
    const swatchBtn = overlay.querySelector<HTMLButtonElement>(`[data-param-swatch="${key}"]`);
    const hiddenInput = overlay.querySelector<HTMLInputElement>(`[data-param="${key}"]`);
    if (!swatchBtn || !hiddenInput) return;

    swatchBtn.addEventListener("click", () => {
      const rgb = hexToRgb(hiddenInput.value) ?? { r: 0, g: 0, b: 0 };
      if (!this.colorPicker) this.colorPicker = new ColorPicker();
      void this.colorPicker.open(rgb, "foreground").then((picked) => {
        if (!picked) return;
        const hex = `#${rgbToHex(picked.r, picked.g, picked.b)}`;
        hiddenInput.value = hex;
        swatchBtn.style.backgroundColor = hex;
        hiddenInput.dispatchEvent(new Event("input", { bubbles: true }));
        this.schedulePreview();
      });
    });
  }

  private renderField(p: FilterParamDef): string {
    const val = p.default;
    if (p.type === "slider") {
      return `
        <div class="filter-dialog__row">
          <label class="filter-dialog__label" data-scrubby="${p.key}">${escapeHtml(p.label)}</label>
          <div class="filter-dialog__slider-row">
            <input type="range" class="filter-dialog__slider" data-param-slider="${p.key}"
              min="${p.min ?? 0}" max="${p.max ?? 100}" step="${p.step ?? 1}" value="${val}" />
            <input type="number" class="filter-dialog__num" data-param="${p.key}"
              min="${p.min ?? 0}" max="${p.max ?? 100}" step="${p.step ?? 1}" value="${val}" />
            ${p.unit ? `<span class="filter-dialog__unit">${escapeHtml(p.unit)}</span>` : ""}
          </div>
        </div>
      `;
    }
    if (p.type === "number") {
      return `
        <div class="filter-dialog__row">
          <label class="filter-dialog__label" data-scrubby="${p.key}">${escapeHtml(p.label)}</label>
          <div class="filter-dialog__slider-row">
            <input type="number" class="filter-dialog__num" data-param="${p.key}"
              min="${p.min ?? 0}" max="${p.max ?? 100}" step="${p.step ?? 1}" value="${val}" />
            ${p.unit ? `<span class="filter-dialog__unit">${escapeHtml(p.unit)}</span>` : ""}
          </div>
        </div>
      `;
    }
    if (p.type === "checkbox") {
      return `
        <div class="filter-dialog__row">
          <label class="filter-dialog__label">${escapeHtml(p.label)}</label>
          <div class="filter-dialog__slider-row">
            <input type="checkbox" class="filter-dialog__check" data-param="${p.key}" ${val ? "checked" : ""} />
          </div>
        </div>
      `;
    }
    if (p.type === "color") {
      // val is expected to be a hex string ("#rrggbb" or "rrggbb"); fall
      // back to black for anything else so the swatch always has a valid
      // CSS color (#i186).
      const raw = typeof val === "string" ? val : "";
      const hex = /^#?[0-9a-fA-F]{6}$/.test(raw) ? (raw.startsWith("#") ? raw : `#${raw}`) : "#000000";
      return `
        <div class="filter-dialog__row">
          <label class="filter-dialog__label">${escapeHtml(p.label)}</label>
          <div class="filter-dialog__slider-row">
            <button type="button" class="filter-dialog__color-swatch" data-param-swatch="${p.key}"
              style="background-color:${escapeHtml(hex)};width:28px;height:20px;border:1px solid rgba(128,128,128,0.6);cursor:pointer;padding:0;"
              aria-label="${escapeHtml(p.label)} color swatch — click to choose a color"></button>
            <input type="hidden" class="filter-dialog__color-value" data-param="${p.key}" value="${escapeHtml(hex)}" />
          </div>
        </div>
      `;
    }
    if (p.type === "dropdown" && p.options) {
      const opts = p.options.map((o) =>
        `<option value="${escapeHtml(o.value)}"${o.value === val ? " selected" : ""}>${escapeHtml(o.label)}</option>`,
      ).join("");
      return `
        <div class="filter-dialog__row">
          <label class="filter-dialog__label">${escapeHtml(p.label)}</label>
          <div class="filter-dialog__slider-row">
            <select class="filter-dialog__select" data-param="${p.key}">${opts}</select>
          </div>
        </div>
      `;
    }
    return "";
  }

  private schedulePreview(): void {
    if (!this.previewCanvas || !this.applyFn) return;
    if (this.previewScheduled) return;
    this.previewScheduled = true;
    requestAnimationFrame(() => {
      this.previewScheduled = false;
      this.updatePreview();
    });
  }

  private updatePreview(): void {
    if (!this.previewCanvas || !this.applyFn || !this.sourcePixels) return;

    // Gather current parameter values
    const currentParams: Record<string, number | boolean> = {};
    for (const p of this.params) {
      const input = this.el?.querySelector<HTMLInputElement>(`[data-param="${p.key}"]`);
      if (!input) continue;
      if (p.type === "checkbox") {
        currentParams[p.key] = input.checked;
      } else if (p.type === "color") {
        // Color values are strings (hex) — not part of the numeric/boolean
        // preview-fn contract, so they're excluded here (#i186).
        continue;
      } else {
        const v = parseFloat(input.value);
        currentParams[p.key] = isFinite(v) ? v : (p.default as number);
      }
    }

    try {
      const filtered = this.applyFn(currentParams);
      const ctx = this.previewCanvas.getContext("2d");
      if (!ctx) return;

      // Draw the filtered result
      const imageData = ctx.createImageData(256, 256);
      // Copy the filtered pixels (may be smaller than 256x256, so we center/scale)
      const srcW = Math.sqrt(filtered.length / 4);
      const srcH = srcW; // assume square for simplicity
      const scaleX = srcW / 256;
      const scaleY = srcH / 256;

      if (srcW <= 256 && srcH <= 256) {
        // Center the image
        const offsetX = Math.floor((256 - srcW) / 2);
        const offsetY = Math.floor((256 - srcH) / 2);
        for (let sy = 0; sy < srcH; sy++) {
          for (let sx = 0; sx < srcW; sx++) {
            const srcIdx = (sy * srcW + sx) * 4;
            const dstIdx = ((sy + offsetY) * 256 + (sx + offsetX)) * 4;
            imageData.data[dstIdx] = filtered[srcIdx]!;
            imageData.data[dstIdx + 1] = filtered[srcIdx + 1]!;
            imageData.data[dstIdx + 2] = filtered[srcIdx + 2]!;
            imageData.data[dstIdx + 3] = filtered[srcIdx + 3]!;
          }
        }
      } else {
        // Scale down: sample from source
        for (let dy = 0; dy < 256; dy++) {
          for (let dx = 0; dx < 256; dx++) {
            const sx = Math.floor(dx * scaleX);
            const sy = Math.floor(dy * scaleY);
            const srcIdx = (sy * srcW + sx) * 4;
            const dstIdx = (dy * 256 + dx) * 4;
            imageData.data[dstIdx] = filtered[srcIdx]!;
            imageData.data[dstIdx + 1] = filtered[srcIdx + 1]!;
            imageData.data[dstIdx + 2] = filtered[srcIdx + 2]!;
            imageData.data[dstIdx + 3] = filtered[srcIdx + 3]!;
          }
        }
      }

      ctx.putImageData(imageData, 0, 0);

      // Draw a split line to show before/after
      ctx.strokeStyle = "rgba(255,255,255,0.3)";
      ctx.lineWidth = 1;
      ctx.setLineDash([4, 4]);
      ctx.beginPath();
      ctx.moveTo(128, 0);
      ctx.lineTo(128, 256);
      ctx.stroke();
      ctx.setLineDash([]);

      // Draw "before" on left half
      const beforeData = ctx.createImageData(128, 256);
      for (let dy = 0; dy < 256; dy++) {
        for (let dx = 0; dx < 128; dx++) {
          const srcSx = Math.floor(dx * scaleX);
          const srcSy = Math.floor(dy * scaleY);
          const srcIdx = (srcSy * srcW + srcSx) * 4;
          const dstIdx = (dy * 128 + dx) * 4;
          beforeData.data[dstIdx] = this.sourcePixels[srcIdx]!;
          beforeData.data[dstIdx + 1] = this.sourcePixels[srcIdx + 1]!;
          beforeData.data[dstIdx + 2] = this.sourcePixels[srcIdx + 2]!;
          beforeData.data[dstIdx + 3] = this.sourcePixels[srcIdx + 3]!;
        }
      }
      ctx.putImageData(beforeData, 0, 0);

      // Labels
      ctx.font = "10px sans-serif";
      ctx.fillStyle = "rgba(255,255,255,0.7)";
      ctx.fillText("Before", 4, 14);
      ctx.fillText("After", 132, 14);
    } catch {
      // Preview failed — silently ignore
    }
  }

  private doOk(params: FilterParamDef[]): void {
    if (!this.el) return;
    const result: FilterDialogResult = {};
    for (const p of params) {
      const input = this.el.querySelector<HTMLInputElement | HTMLSelectElement>(`[data-param="${p.key}"]`);
      if (!input) continue;
      if (p.type === "checkbox") {
        result[p.key] = (input as HTMLInputElement).checked;
      } else if (p.type === "dropdown") {
        result[p.key] = (input as HTMLSelectElement).value;
      } else if (p.type === "color") {
        // Hex string, e.g. "#ff8800" — read as-is, no numeric parsing (#i186).
        result[p.key] = (input as HTMLInputElement).value;
      } else {
        const v = parseFloat(input.value);
        result[p.key] = isFinite(v) ? v : p.default;
      }
    }
    // Capture resolve into a local before close() nulls this.resolve,
    // so the promise always settles even if doOk is called redundantly.
    const resolve = this.resolve;
    this.resolve = null;
    this.close();
    resolve?.(result);
  }

  private doCancel(): void {
    // Capture resolve into a local before close() nulls this.resolve.
    const resolve = this.resolve;
    this.resolve = null;
    this.close();
    resolve?.(null);
  }

  /**
   * Alt-Reset: restore all field values to their initial (default) values
   * without closing the dialog (#225).
   */
  private doReset(): void {
    if (!this.el) return;
    for (const p of this.params) {
      const input = this.el.querySelector<HTMLInputElement | HTMLSelectElement>(`[data-param="${p.key}"]`);
      if (!input) continue;
      if (p.type === "checkbox") {
        (input as HTMLInputElement).checked = Boolean(this.initialValues[p.key]);
      } else if (p.type === "color") {
        const hex = String(this.initialValues[p.key]);
        (input as HTMLInputElement).value = hex;
        const swatch = this.el.querySelector<HTMLButtonElement>(`[data-param-swatch="${p.key}"]`);
        if (swatch) swatch.style.backgroundColor = hex;
      } else {
        input.value = String(this.initialValues[p.key]);
        // Sync slider if present.
        const sliderInput = this.el.querySelector<HTMLInputElement>(`[data-param-slider="${p.key}"]`);
        if (sliderInput) sliderInput.value = String(this.initialValues[p.key]);
      }
      input.dispatchEvent(new Event("input", { bubbles: true }));
    }
    this.schedulePreview();
  }

  private close(): void {
    if (this.el) {
      // Remove keyboard listeners that were attached to document (#225).
      const el = this.el as HTMLDivElement & {
        _onKey?: (e: KeyboardEvent) => void;
        _onAltDown?: (e: KeyboardEvent) => void;
        _onAltUp?: (e: KeyboardEvent) => void;
      };
      if (el._onKey) document.removeEventListener("keydown", el._onKey);
      if (el._onAltDown) document.removeEventListener("keydown", el._onAltDown);
      if (el._onAltUp) document.removeEventListener("keyup", el._onAltUp);
    }
    if (this.el && this.el.parentNode) {
      this.el.parentNode.removeChild(this.el);
    }
    this.el = null;
    this.resolve = null;
    this.previewCanvas = null;
    this.sourcePixels = null;
    this.applyFn = null;
    this.keyHandler = null;
    this.altHeld = false;

    // Restore focus to the element that was focused before the dialog opened (#225).
    if (this.openerEl && "focus" in this.openerEl) {
      (this.openerEl as HTMLElement).focus();
    }
    this.openerEl = null;
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c] ?? c));
}
